import type { CanonicalEntity, UniversalBundle } from '@/shared/api/types';

const SHA256 = /^[0-9a-f]{64}$/;

function domain(entity: CanonicalEntity): Record<string, unknown> {
  return entity.domain && typeof entity.domain === 'object'
    ? entity.domain as Record<string, unknown> : {};
}

function role(entity: CanonicalEntity): string {
  return String((entity as CanonicalEntity & { role?: unknown }).role || '');
}

function exactMotionContract(entity: CanonicalEntity, revisionSha256: string): boolean {
  const value = domain(entity).motionContract;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const motion = value as Record<string, unknown>;
  if (Object.keys(motion).sort().join(',') !== [
    'authority', 'direction', 'openDistanceMm', 'owner', 'schema', 'slideAxis',
    'sourceEntityId', 'sourceRevisionSha256', 'type',
  ].sort().join(',')) return false;
  return motion.schema === 'holzcom.motion_contract.v1'
    && motion.authority === 'explicit_graph3_review'
    && motion.owner === 'drawer_front'
    && motion.type === 'drawer'
    && ['x', 'y', 'z'].includes(String(motion.slideAxis))
    && ['positive', 'negative'].includes(String(motion.direction))
    && typeof motion.openDistanceMm === 'number'
    && Number.isFinite(motion.openDistanceMm)
    && motion.openDistanceMm >= 50
    && motion.openDistanceMm <= 1000
    && motion.sourceEntityId === entity.id
    && motion.sourceRevisionSha256 === revisionSha256;
}

/** Validate the closed reviewed PRO100 kinematics cohort before SceneCanvas uses it. */
export function validatedPro100MotionOwnerIds(bundle: UniversalBundle): Set<string> {
  if (bundle.sourceKind !== 'pro100_decode') return new Set();
  const entities = bundle.canonicalScene?.entities ?? [];
  const review = bundle.pro100Review;
  if (!review || review.schema !== 'holzcom.pro100.review-publication.v1'
    || review.kinematicsStatus !== bundle.kinematicsStatus
    || !SHA256.test(review.sourceRevisionSha256)
    || !Array.isArray(review.motionOwnerEntityIds)
    || new Set(review.motionOwnerEntityIds).size !== review.motionOwnerEntityIds.length
    || review.motionOwnerEntityIds.some((id) => typeof id !== 'string' || !id)) {
    throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
  }
  const owners = new Set(review.motionOwnerEntityIds);
  const byId = new Map(entities.map((entity) => [entity.id, entity]));
  const sourceMotionOwners = entities.filter((entity) => {
    const rowDomain = domain(entity);
    return rowDomain.sourceAuthority === 'pro100_decode' && rowDomain.motionContract !== undefined;
  });
  if (bundle.kinematicsStatus === 'not_reviewed') {
    if (owners.size || sourceMotionOwners.length
      || entities.some((entity) => domain(entity).graph3Generated === true)) {
      throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
    }
    return owners;
  }
  if (bundle.kinematicsStatus !== 'reviewed' || owners.size === 0
    || sourceMotionOwners.length !== owners.size) {
    throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
  }
  for (const ownerId of owners) {
    const owner = byId.get(ownerId);
    if (!owner || role(owner) !== 'drawer_front'
      || domain(owner).sourceAuthority !== 'pro100_decode'
      || !exactMotionContract(owner, review.sourceRevisionSha256)) {
      throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
    }
  }
  const generated = entities.filter((entity) => domain(entity).graph3Generated === true);
  for (const ownerId of owners) {
    const owned = generated.filter((entity) => domain(entity).sourceEntityId === ownerId);
    const moving = owned.filter((entity) => ['drawer_box', 'drawer_carga_component'].includes(role(entity)));
    const runners = owned.filter((entity) => role(entity) === 'drawer_slide');
    const slots = owned.map((entity) => {
      const assembly = domain(entity).drawerAssembly;
      return assembly && typeof assembly === 'object' && !Array.isArray(assembly)
        && (assembly as Record<string, unknown>).ownerId === ownerId
        ? (assembly as Record<string, unknown>).slot : null;
    }).sort();
    if (owned.length !== 5 || moving.length !== 3 || runners.length !== 2
      || slots.join('\0') !== ['box', 'carga-left', 'carga-right', 'runner-left', 'runner-right'].sort().join('\0')) {
      throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
    }
    for (const entity of owned) {
      const rowDomain = domain(entity);
      if (rowDomain.sourceRevisionSha256 !== review.sourceRevisionSha256
        || rowDomain.authority !== 'reviewed_graph3_preview_only') {
        throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
      }
      const visibility = rowDomain.visibilityContract;
      if (['drawer_box', 'drawer_carga_component'].includes(role(entity))) {
        if (!visibility || typeof visibility !== 'object'
          || (visibility as Record<string, unknown>).ownerId !== ownerId
          || (visibility as Record<string, unknown>).motionBinding !== 'drawer_moving') {
          throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
        }
      } else if (visibility !== undefined) {
        throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
      }
    }
  }
  if (generated.some((entity) => !owners.has(String(domain(entity).sourceEntityId || '')))) {
    throw new Error('PRO100_KINEMATICS_CONTRACT_INVALID');
  }
  return owners;
}
